/**
 * Section spacing drawer (Phase 5 of pb-section-properties overhaul).
 *
 * Renders a vertical-slab box-model diagram for the four section-level
 * spacing overrides (marginTop / paddingTop / paddingBottom / marginBottom)
 * with scrubbable bands, click-to-preset, and inline edit.
 *
 * Why a section-tailored diagram instead of reusing `BoxModelWidget`:
 *
 * A section IS a horizontal slab — it has top + bottom spacing, never sides.
 * A vertical-stack diagram (margin-top → padding-top → content slab →
 * padding-bottom → margin-bottom) maps directly onto that mental model and
 * onto `section.overrides.{marginTop,paddingTop,paddingBottom,marginBottom}`.
 * Reskinning the 4-side element widget would force the user to ignore the
 * left and right zones; this is more honest and the visual is more striking.
 *
 * Reuses:
 *   - `value-scrubber.js` for pointer drag lifecycle + Esc cancellation
 *   - `applySectionOverride` for the five-emission contract on every commit
 *   - `SPACING_PRESETS` for the preset chooser popover
 *   - `openPopoverMenu` for the preset chooser
 *
 * Drag convention:
 *   - horizontal scrub only
 *   - drag right grows, drag left shrinks
 *   - 1rem per 64px of pointer travel, snapped to 0.5rem
 */

import { applySectionOverride, applySectionOverridesBulk } from './apply-section-override.js';
import { eventBus } from '../../core/events.js';
import { store } from '../../core/state.js';
import { bridge } from '../../editing/bridge.js';
import { beginScrub } from '../../editing/value-scrubber.js';
import { SPACING_PRESETS } from '../../shared/spacing-presets.js';
import { escapeHTML as escapeHtml } from '../../shared/html-escape.js';
import { openPopoverMenu, closeActivePopoverMenu } from '../popover-menu.js';
import {
  getActiveViewportName,
  getResponsiveEditScope,
  getResponsiveOverrideViewport,
  setResponsiveEditScope,
  viewportLabel
} from '../viewport-controls.js';
import { getSectionOverridesForViewport } from '../../editing/section-utils.js';

const BAND_FIELDS = Object.freeze(['marginTop', 'paddingTop', 'paddingBottom', 'marginBottom']);
const FIELD_LABELS = Object.freeze({
  marginTop: 'Margin top',
  paddingTop: 'Padding top',
  paddingBottom: 'Padding bottom',
  marginBottom: 'Margin bottom'
});
const FIELD_KIND = Object.freeze({
  marginTop: 'margin',
  paddingTop: 'padding',
  paddingBottom: 'padding',
  marginBottom: 'margin'
});
const LINK_PAIRS = Object.freeze({
  marginTop: 'marginBottom',
  marginBottom: 'marginTop',
  paddingTop: 'paddingBottom',
  paddingBottom: 'paddingTop'
});
const LINK_STORAGE_KEY = 'pb-section-link-spacing';
const DRAG_START_THRESHOLD_PX = 4;
const ROOT_PX = 16;
const DRAG_PX_PER_REM = 64;
const KEYBOARD_STEP_REM = 0.5;

function snapHalfRem(px, rootPx = ROOT_PX) {
  const stepPx = rootPx * KEYBOARD_STEP_REM;
  if (!Number.isFinite(px) || stepPx <= 0) return 0;
  return Math.round(px / stepPx) * stepPx;
}

function clampFieldPx(field, px) {
  const min = field.startsWith('padding') ? 0 : -512;
  return Math.max(min, Math.min(512, px));
}

function formatRemPx(px, rootPx = ROOT_PX) {
  const rem = (px || 0) / rootPx;
  const rounded = Math.round(rem * 1000) / 1000;
  return `${rounded}rem`;
}

function formatRemDisplay(px, rootPx = ROOT_PX) {
  const rem = (px || 0) / rootPx;
  if (Math.abs(rem) < 0.001) return '0';
  const rounded = Math.round(rem * 1000) / 1000;
  // Trim trailing zeros: 1.0 → "1", 1.25 → "1.25"
  const text = Number.isInteger(rounded)
    ? String(rounded)
    : String(rounded).replace(/0+$/, '').replace(/\.$/, '');
  return text;
}

/** Validate a spacing value with the browser's CSS parser. */
export function isValidSpacingCssValue(value, field = 'marginTop') {
  const raw = String(value || '').trim();
  if (!raw) return true;
  const property = field.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);
  try {
    if (typeof CSSStyleSheet === 'function') {
      const sheet = new CSSStyleSheet();
      sheet.replaceSync(`.pb-spacing-value { ${property}: ${raw}; }`);
      const rule = sheet.cssRules?.[0];
      return !!rule?.style?.getPropertyValue(property)?.trim();
    }
    if (typeof document !== 'undefined') {
      const probe = document.createElement('div');
      probe.style.setProperty(property, raw);
      return !!probe.style.getPropertyValue(property).trim();
    }
  } catch {
    return false;
  }
  return false;
}

/** Map a px value → band visual height. Caps so the diagram never collapses
 *  or blows out of the drawer. 0px → 12px, 64px → 28px, 192px+ → 60px. */
function bandVisualHeight(px) {
  const clamped = Math.max(0, Math.min(192, px));
  return Math.round(12 + (clamped / 192) * 48); // 12..60
}

function readLinkPref() {
  try {
    return localStorage.getItem(LINK_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}
function writeLinkPref(value) {
  try {
    localStorage.setItem(LINK_STORAGE_KEY, value ? '1' : '0');
  } catch {
    /* noop */
  }
}

/**
 * Mount the spacing drawer body into `container` and wire all interactions.
 *
 * Multi-select (16B2): pass `instanceIds` (+ `leadId`) to drive N sections.
 * The semantics split is deliberate, per the plan's product decision:
 *   - typed values, presets and reset are ABSOLUTE — every selected section
 *     gets the value;
 *   - drag-scrub and arrow-nudge are RELATIVE — each section moves by the
 *     same delta from ITS OWN base, so a mixed selection keeps its rhythm.
 * A band whose sections disagree shows `—` ("Mixed values") and stays fully
 * interactive; touching it applies to all.
 *
 * @param {HTMLElement} container - drawer body element
 * @param {{ instanceId: string, instanceIds?: string[], leadId?: string }} ctx
 * @returns {() => void} teardown function (drawer.js invokes it on swap/close)
 */
export function renderSectionSpacingDrawer(container, { instanceId, instanceIds, leadId }) {
  if (!container || !instanceId) {
    console.warn('[section-spacing-drawer] missing container/instanceId');
    return () => {};
  }

  const targets =
    Array.isArray(instanceIds) && instanceIds.length > 1 ? instanceIds.slice() : [instanceId];
  const multi = targets.length > 1;
  const lead = leadId || instanceId;

  let rootPx = ROOT_PX;
  let disposed = false;
  const conversions = new Map();
  const projectGeneration = store.getEpoch() - store.getRevision();
  let linked = readLinkPref();
  let activeScrub = null;
  let activeEdit = null;

  function resolveSection() {
    if (store.getEpoch() - store.getRevision() !== projectGeneration) return null;
    const page = store.getActivePage();
    if (!page || !Array.isArray(page.sections)) return null;
    return page.sections.find((s) => s.instanceId === (multi ? lead : instanceId)) || null;
  }

  function resolveTargetSections() {
    if (store.getEpoch() - store.getRevision() !== projectGeneration) return [];
    const page = store.getActivePage();
    if (!page || !Array.isArray(page.sections)) return [];
    const byId = new Map(page.sections.map((s) => [s.instanceId, s]));
    return targets.map((id) => byId.get(id)).filter(Boolean);
  }

  /** Effective per-viewport value for one section — what the user SEES. */
  function effectiveValue(section, field) {
    const overrides = getSectionOverridesForViewport(section, getActiveViewportName());
    return overrides?.[field] || '';
  }

  /** Declaration provenance and a renderer measurement are independent facts. */
  function resolveValue(section, field) {
    const declared = effectiveValue(section, field);
    const measurement = section ? bridge.getSectionSpacing(section.instanceId) : null;
    const raw = measurement?.values?.[field];
    const measuredPx =
      typeof raw === 'string' && raw.endsWith('px') ? Number(raw.slice(0, -2)) : null;
    const fresh = measuredPx !== null && Number.isFinite(measuredPx) && measurement.rootPx > 0;
    return {
      source: declared ? 'override' : fresh ? 'inherited' : 'unknown',
      declared,
      measuredPx,
      fresh,
      rootPx: measurement?.rootPx,
      measurement
    };
  }

  function fieldModels(field) {
    return resolveTargetSections().map((section) => resolveValue(section, field));
  }

  function fieldMixedState(field) {
    const models = fieldModels(field);
    if (!multi || models.length !== targets.length || models.some((model) => !model.fresh))
      return null;
    return models.some((model) => Math.abs(model.measuredPx - models[0].measuredPx) > 0.01)
      ? 'mixed'
      : null;
  }

  function requestMeasurement({ invalidate = false } = {}) {
    if (disposed) return;
    if (invalidate) bridge.invalidateSectionSpacing();
    bridge.querySectionSpacing();
    refresh();
  }

  function refuseUnmeasured() {
    const hint = container.querySelector('.pb-spsd-hint');
    if (hint)
      hint.textContent = 'Measuring… Wait for the current section spacing before adjusting.';
    refresh();
  }

  function getOverrides() {
    const sec = resolveSection();
    return sec ? getSectionOverridesForViewport(sec, getActiveViewportName()) : {};
  }

  function getExplicitOverrides() {
    const sec = resolveSection();
    if (!sec) return {};
    const viewport = getResponsiveOverrideViewport();
    return viewport ? sec.overrides?.responsive?.[viewport] || {} : sec.overrides || {};
  }

  /** ABSOLUTE write — typed / preset / reset paths. */
  function applyAbsolute(fields, value, source) {
    if (!resolveSection() || resolveTargetSections().length !== targets.length) return;
    if (multi) {
      const changes = {};
      for (const f of fields) changes[f] = value;
      applySectionOverridesBulk(targets, changes, {
        source,
        viewport: getResponsiveOverrideViewport(),
        lead,
        transaction: 'auto'
      });
      return;
    }
    const viewport = getResponsiveOverrideViewport();
    if (fields.length > 1) {
      store.beginTransaction('Adjust section spacing');
      for (const f of fields) {
        applySectionOverride(instanceId, f, value, { source, viewport });
      }
      store.commitTransaction();
    } else {
      applySectionOverride(instanceId, fields[0], value, { source, viewport });
    }
  }

  /**
   * RELATIVE write — scrub / nudge paths. Each section moves by `deltaPx`
   * from its own base (captured in `bases`: Map instanceId → Map field → px).
   */
  function applyRelative(fields, bases, deltaPx, source, { ambient = false } = {}) {
    if (!resolveSection() || resolveTargetSections().length !== targets.length) return;
    const viewport = getResponsiveOverrideViewport();
    const changesFor = (section, id) => {
      const perField = {};
      const own = bases.get(id);
      for (const f of fields) {
        const basePx = own.get(f);
        const nextPx = clampFieldPx(f, snapHalfRem(basePx + deltaPx, rootPx));
        perField[f] = Math.abs(nextPx) <= 0.001 ? '0' : formatRemPx(nextPx, rootPx);
        const declared = effectiveValue(section, f);
        if (declared) conversions.set(f, `was ${declared}, now ${perField[f]}`);
      }
      return perField;
    };
    if (multi) {
      applySectionOverridesBulk(targets, changesFor, {
        source,
        viewport,
        lead,
        transaction: ambient ? 'ambient' : 'auto'
      });
      return;
    }
    const section = resolveSection();
    const perField = changesFor(section, instanceId);
    for (const f of fields) {
      applySectionOverride(instanceId, f, perField[f], { source, viewport });
    }
  }

  /** A relative gesture either has every measured base or makes no write. */
  function captureBases(fields) {
    const sections = resolveTargetSections();
    if (sections.length !== targets.length) return null;
    const bases = new Map();
    let measuredRoot = null;
    for (const section of sections) {
      const own = new Map();
      for (const field of fields) {
        const model = resolveValue(section, field);
        if (!model.fresh || (measuredRoot !== null && measuredRoot !== model.rootPx)) return null;
        measuredRoot = model.rootPx;
        own.set(field, model.measuredPx);
      }
      bases.set(section.instanceId, own);
    }
    rootPx = measuredRoot;
    return bases;
  }

  // ── DOM ────────────────────────────────────────────────────────────────
  container.innerHTML = `
    <div class="pb-spsd" data-section-spacing-drawer>
      <div class="pb-spsd-diagram" role="group" aria-label="Section spacing">
        ${BAND_FIELDS.slice(0, 2).map(renderBandHTML).join('')}
        <div class="pb-spsd-content" aria-hidden="true">
          <span class="pb-spsd-content-label">Content</span>
        </div>
        ${BAND_FIELDS.slice(2).map(renderBandHTML).join('')}
      </div>
      <div class="pb-spsd-toolbar">
        <button type="button" class="pb-spsd-link" aria-pressed="${linked ? 'true' : 'false'}"
                data-section-spacing-link
                aria-label="Link top and bottom spacing">
          <span class="pb-spsd-link-icon" aria-hidden="true">${linked ? chainLockedSvg() : chainOpenSvg()}</span>
          <span>Link top &amp; bottom</span>
        </button>
        <button type="button" class="pb-spsd-reset-all" data-section-spacing-reset
                aria-label="Reset all spacing">
          <span aria-hidden="true">⟲</span>
          <span>Reset all</span>
        </button>
      </div>
      <div class="pb-spsd-scope" role="group" aria-label="Responsive edit scope">
        <div class="pb-spsd-scope-options">
          <button type="button" data-responsive-scope="viewport"></button>
          <button type="button" data-responsive-scope="all">All sizes</button>
        </div>
      </div>
      <p class="pb-spsd-hint" role="status" aria-live="polite">
        Drag horizontally to scrub · Click for a preset
      </p>
    </div>
  `;

  // ── Per-band wiring ────────────────────────────────────────────────────
  const bandEls = new Map();
  const labelEls = new Map();
  for (const field of BAND_FIELDS) {
    const band = container.querySelector(`[data-spsd-field="${field}"]`);
    if (!band) continue;
    bandEls.set(field, band);
    const label = band.querySelector('.pb-spsd-band-label');
    labelEls.set(field, label);

    band.addEventListener('pointerdown', (e) => onBandPointerDown(e, field));
    band.addEventListener('keydown', (e) => onBandKeyDown(e, field));
    if (label) {
      label.addEventListener('dblclick', (e) => {
        e.preventDefault();
        e.stopPropagation();
        beginEdit(field);
      });
    }
  }

  const linkBtn = container.querySelector('[data-section-spacing-link]');
  linkBtn?.addEventListener('click', () => {
    linked = !linked;
    writeLinkPref(linked);
    linkBtn.setAttribute('aria-pressed', linked ? 'true' : 'false');
    const icon = linkBtn.querySelector('.pb-spsd-link-icon');
    if (icon) icon.innerHTML = linked ? chainLockedSvg() : chainOpenSvg();
    container
      .querySelector('[data-section-spacing-drawer]')
      ?.classList.toggle('pb-spsd-linked', linked);
  });
  if (linked) {
    container.querySelector('[data-section-spacing-drawer]')?.classList.add('pb-spsd-linked');
  }

  const resetBtn = container.querySelector('[data-section-spacing-reset]');
  resetBtn?.addEventListener('click', () => {
    if (multi) {
      // ABSOLUTE clear across the whole selection; unchanged sections are
      // skipped inside computeSectionOverrides, so this is one no-op-safe
      // write with one history entry.
      const before = anyExplicitSpacingSet();
      applyAbsolute(BAND_FIELDS, '', 'spacing-drawer-reset-all');
      if (!before) flashResetNoop();
      return;
    }
    const overrides = getExplicitOverrides();
    const viewport = getResponsiveOverrideViewport();
    const cleared = [];
    store.beginTransaction('Reset section spacing');
    for (const field of BAND_FIELDS) {
      if (overrides[field] != null && overrides[field] !== '') {
        applySectionOverride(instanceId, field, '', {
          source: 'spacing-drawer-reset-all',
          viewport
        });
        cleared.push(field);
      }
    }
    store.commitTransaction();
    if (cleared.length === 0) flashResetNoop();
  });

  function flashResetNoop() {
    // No-op; animate the button briefly to show the click registered.
    resetBtn.classList.add('pb-spsd-reset-noop');
    setTimeout(() => resetBtn.classList.remove('pb-spsd-reset-noop'), 280);
  }

  /** Any explicit spacing override set on ANY target (multi reset gating). */
  function anyExplicitSpacingSet() {
    const viewport = getResponsiveOverrideViewport();
    for (const section of resolveTargetSections()) {
      const explicit = viewport
        ? section.overrides?.responsive?.[viewport] || {}
        : section.overrides || {};
      for (const field of BAND_FIELDS) {
        if (explicit[field] != null && explicit[field] !== '') return true;
      }
    }
    return false;
  }

  const scopeButtons = Array.from(container.querySelectorAll('[data-responsive-scope]'));
  for (const button of scopeButtons) {
    button.addEventListener('click', () => setResponsiveEditScope(button.dataset.responsiveScope));
  }

  requestMeasurement();

  // ── Live state sync ────────────────────────────────────────────────────
  const onOverrideChanged = ({ instanceId: id, field } = {}) => {
    if (id && !targets.includes(id)) return;
    if (field && !BAND_FIELDS.includes(field)) return;
    requestMeasurement();
  };
  // `state-changed` covers undo / redo / programmatic resets that go through
  // the store but not through eventBus events.
  const onStateChanged = () => {
    if (store.getEpoch() - store.getRevision() !== projectGeneration) {
      if (!activeScrub?.ending) activeScrub?.handle?.cancel();
      activeEdit = null;
    }
    requestMeasurement();
  };
  const onResponsiveChanged = () => {
    activeScrub?.handle?.cancel();
    requestMeasurement({ invalidate: true });
  };
  const onBridgeReady = () => {
    if (!activeScrub?.ending) activeScrub?.handle?.cancel();
    requestMeasurement();
  };
  const onMeasurement = () => refresh();
  eventBus.on('theme:override-changed', onOverrideChanged);
  eventBus.on('state-changed', onStateChanged);
  eventBus.on('viewport:changed', onResponsiveChanged);
  eventBus.on('responsive-edit-scope:changed', onResponsiveChanged);
  eventBus.on('iframe:bridge-ready', onBridgeReady);
  eventBus.on('iframe:section-spacing-response', onMeasurement);

  // ── Helpers (closure-scoped) ──────────────────────────────────────────

  function refresh() {
    const explicit = getExplicitOverrides();
    const scope = getResponsiveEditScope();
    const viewport = getActiveViewportName();
    for (const button of scopeButtons) {
      const active = button.dataset.responsiveScope === scope;
      button.classList.toggle('pb-active', active);
      button.setAttribute('aria-pressed', String(active));
      // The viewport option NAMES the size it writes to (see glass-panel-text).
      if (button.dataset.responsiveScope === 'viewport') {
        const name = viewportLabel(viewport);
        button.textContent = name;
        button.dataset.tooltip = `Apply spacing changes to ${name} only`;
      }
    }
    let anySet = false;
    for (const field of BAND_FIELDS) {
      const model = resolveValue(resolveSection(), field);
      const models = fieldModels(field);
      const fresh = models.length === targets.length && models.every((item) => item.fresh);
      const value = model.declared;
      const px = model.fresh ? model.measuredPx : null;
      if (model.fresh) rootPx = model.rootPx;
      if (explicit[field] != null && explicit[field] !== '') anySet = true;
      const band = bandEls.get(field);
      const label = labelEls.get(field);
      const mixed = fieldMixedState(field) === 'mixed';
      const unit = band?.querySelector('.pb-spsd-band-unit');
      if (unit) unit.hidden = !!value || !fresh;
      if (band) {
        band.style.setProperty('--pb-spsd-band-h', `${px === null ? 28 : bandVisualHeight(px)}px`);
        band.setAttribute('aria-busy', String(!fresh));
        band.classList.toggle('pb-spsd-band-unknown', !fresh);
        band.classList.toggle('pb-spsd-band-inherited', model.source === 'inherited');
        band.classList.toggle('pb-spsd-band-zero', fresh && !mixed && px <= 0.001);
        // Mixed-value contract (16B2): `—`, labelled, still fully interactive.
        if (mixed) band.dataset.mixed = 'true';
        else delete band.dataset.mixed;
      }
      if (label && activeEdit?.field !== field) {
        if (!fresh) {
          label.textContent = value || 'Measuring…';
          band.removeAttribute('aria-valuenow');
          band.setAttribute(
            'aria-valuetext',
            `${conversions.get(field) || value || ''}${value ? '; ' : ''}Measuring…`
          );
          band.setAttribute('aria-label', `${FIELD_LABELS[field]}, Measuring…`);
        } else if (mixed) {
          label.textContent = '—';
          band?.removeAttribute('aria-valuenow');
          band?.setAttribute('aria-valuetext', 'Mixed values');
          band?.setAttribute(
            'aria-label',
            `${FIELD_LABELS[field]}, Mixed values across the selection — adjusting applies to all, drag horizontally to adjust or press Enter for presets`
          );
        } else {
          label.textContent = value || formatRemDisplay(px, rootPx);
          const valueText =
            conversions.get(field) ||
            (value
              ? `${value}, rendered ${formatRemDisplay(px, rootPx)} rem`
              : `${formatRemDisplay(px, rootPx)} rem, inherited`);
          band?.setAttribute('aria-valuenow', String(Math.round((px / rootPx) * 1000) / 1000));
          band?.setAttribute('aria-valuetext', valueText);
          band?.setAttribute(
            'aria-label',
            `${FIELD_LABELS[field]}, current value ${valueText}, drag horizontally to adjust or press Enter for presets`
          );
        }
      }
    }
    if (multi) anySet = anyExplicitSpacingSet();
    if (resetBtn) {
      resetBtn.classList.toggle('pb-spsd-reset-active', anySet);
      resetBtn.disabled = !anySet;
    }
  }

  function onBandPointerDown(e, field) {
    if (e.button !== 0) return;
    if (e.target?.closest('input, button, [contenteditable]')) return;
    if (activeEdit) commitEdit();
    e.preventDefault();
    bandEls.get(field)?.focus({ preventScroll: true });

    const startX = e.clientX;
    const startY = e.clientY;
    let scrubStarted = false;
    let cleaned = false;

    const onMove = (ev) => {
      if (scrubStarted) return;
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      if (Math.abs(dx) >= DRAG_START_THRESHOLD_PX && Math.abs(dx) >= Math.abs(dy)) {
        scrubStarted = true;
        cleanupListeners();
        startScrub(field, e);
      }
    };
    const onUp = () => {
      cleanupListeners();
      if (!scrubStarted) {
        // Click without drag → preset chooser
        openPresetMenu(field);
      }
    };
    const cleanupListeners = () => {
      if (cleaned) return;
      cleaned = true;
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp, { once: true });
  }

  function startScrub(field, originalEvent) {
    if (activeScrub) return;
    const scrubFields = linked && LINK_PAIRS[field] ? [field, LINK_PAIRS[field]] : [field];
    const bases = captureBases(scrubFields);
    if (!bases) {
      refuseUnmeasured();
      return;
    }
    const startPx = bases.get(multi ? lead : instanceId).get(field);

    const drawerEl = container.querySelector('.pb-spsd');
    drawerEl?.classList.add('pb-spsd-dragging', `pb-spsd-dragging-${FIELD_KIND[field]}`);
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';

    store.beginTransaction('Adjust section spacing');
    activeScrub = {
      field,
      startPx,
      lastPx: startPx,
      pendingFrame: null,
      pendingPayload: null,
      // Multi (16B2): a scrub is a RELATIVE delta — every selected section
      // moves from its own base, captured once at gesture start.
      bases,
      pendingDeltaPx: null
    };

    const flushPending = () => {
      if (!activeScrub) return;
      activeScrub.pendingFrame = null;
      const deltaPx = activeScrub.pendingDeltaPx;
      if (deltaPx == null) return;
      activeScrub.pendingDeltaPx = null;
      applyRelative(scrubFields, activeScrub.bases, deltaPx, 'spacing-drawer-scrub', {
        ambient: true
      });
    };

    activeScrub.handle = beginScrub({
      event: originalEvent,
      axis: 'x',
      startPx,
      zoom: 1, // section drawer is in parent-frame coords
      sign: 1,
      min: -4096,
      max: 4096,
      snap: false,
      onMove: ({ delta }) => {
        if (!activeScrub) return;
        const deltaPx = delta * (rootPx / DRAG_PX_PER_REM);
        const gainedPx = activeScrub.startPx + deltaPx;
        const nextPx = clampFieldPx(field, snapHalfRem(gainedPx, rootPx));
        activeScrub.lastPx = nextPx;
        // rAF-throttle: coalesce pointermove storms (often >120Hz on modern
        // pointers) into one override write per frame so the bridge doesn't
        // get hammered.
        activeScrub.pendingDeltaPx = deltaPx;
        if (activeScrub.pendingFrame == null) {
          activeScrub.pendingFrame = requestAnimationFrame(flushPending);
        }
        pulseSnap(field);
        if (nextPx <= 0 && field.startsWith('padding')) flashClampReject(field);
      },
      onEnd: () => endScrub({ commit: true, flushPending }),
      onCancel: () => endScrub({ commit: false, flushPending })
    });
  }

  function endScrub({ commit, flushPending }) {
    if (activeScrub) activeScrub.ending = true;
    const drawerEl = container.querySelector('.pb-spsd');
    drawerEl?.classList.remove(
      'pb-spsd-dragging',
      'pb-spsd-dragging-margin',
      'pb-spsd-dragging-padding'
    );
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    if (activeScrub?.pendingFrame != null) {
      cancelAnimationFrame(activeScrub.pendingFrame);
      activeScrub.pendingFrame = null;
    }
    if (commit) {
      // Flush any final pending payload so the committed transaction
      // contains the user's last value, not the second-to-last.
      if (typeof flushPending === 'function') flushPending();
      store.commitTransaction();
    } else {
      store.abortTransaction();
      eventBus.emit('theme:override-changed');
    }
    activeScrub = null;
  }

  function pulseSnap(field) {
    const label = labelEls.get(field);
    if (!label) return;
    label.classList.remove('pb-spsd-label-snap-pulse');
    void label.offsetWidth; // restart animation
    label.classList.add('pb-spsd-label-snap-pulse');
    setTimeout(() => label.classList.remove('pb-spsd-label-snap-pulse'), 260);
  }

  function flashClampReject(field) {
    const band = bandEls.get(field);
    if (!band) return;
    band.classList.remove('pb-spsd-band-clamp-reject');
    void band.offsetWidth;
    band.classList.add('pb-spsd-band-clamp-reject');
    setTimeout(() => band.classList.remove('pb-spsd-band-clamp-reject'), 320);
  }

  function flashInvalidValue(field, input) {
    const band = bandEls.get(field);
    if (!band || !input) return;
    input.setAttribute('aria-invalid', 'true');
    input.dataset.tooltip = 'Enter a valid CSS spacing value';
    band.classList.remove('pb-spsd-band-invalid');
    void band.offsetWidth;
    band.classList.add('pb-spsd-band-invalid');
    setTimeout(() => {
      band.classList.remove('pb-spsd-band-invalid');
      refresh();
    }, 900);
  }

  function openPresetMenu(field) {
    const band = bandEls.get(field);
    if (!band) return;
    closeActivePopoverMenu();

    const overrides = getOverrides();
    const currentValue = overrides[field] || '';

    const items = SPACING_PRESETS.map((preset) => ({
      id: preset.name.toLowerCase(),
      label: preset.name,
      type: 'radio',
      checked:
        fieldMixedState(field) !== 'mixed' &&
        (preset.value === currentValue ||
          (resolveValue(resolveSection(), field).fresh &&
            Math.abs(
              parseFloat(resolveValue(resolveSection(), field).measurement.presets[preset.value]) -
                resolveValue(resolveSection(), field).measuredPx
            ) < 0.01)),
      onSelect: () => commitValue(field, preset.value)
    }));
    items.push({ type: 'separator' });
    items.push({
      id: 'custom-edit',
      label: '✎  Edit value…',
      onSelect: () => beginEdit(field)
    });
    if (currentValue) {
      items.push({ type: 'separator' });
      items.push({
        id: 'reset',
        label: '⟲  Reset',
        onSelect: () => commitValue(field, '')
      });
    }

    openPopoverMenu(band, {
      menuLabel: `${FIELD_LABELS[field]} preset`,
      align: 'left',
      width: 200,
      consumeCanvasDismiss: true,
      items
    });
  }

  function commitValue(field, value) {
    // Typed values and presets are ABSOLUTE: every selected section gets the
    // value (the natural reading of "make these 2rem").
    const fields = linked && LINK_PAIRS[field] ? [field, LINK_PAIRS[field]] : [field];
    applyAbsolute(fields, value, 'spacing-drawer-preset');
  }

  function beginEdit(field) {
    if (activeScrub) return;
    if (activeEdit) commitEdit();
    const label = labelEls.get(field);
    if (!label) return;
    const model = resolveValue(resolveSection(), field);
    if (!model.declared && !model.fresh) {
      refuseUnmeasured();
      return;
    }
    if (model.fresh) rootPx = model.rootPx;
    const startText = model.declared || formatRemDisplay(model.measuredPx, rootPx);

    label.textContent = '';
    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'pb-spsd-edit-input';
    input.inputMode = 'decimal';
    input.spellcheck = false;
    input.value = startText;
    input.setAttribute('aria-label', `${FIELD_LABELS[field]} value in rem`);
    label.appendChild(input);
    input.focus();
    input.select();

    activeEdit = { field, input };

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commitEdit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        cancelEdit();
      }
    });
    input.addEventListener('blur', () => commitEdit());
  }

  function commitEdit() {
    if (!activeEdit) return;
    const { field, input } = activeEdit;
    const raw = input.value.trim();
    activeEdit = null;
    if (!raw) {
      commitValue(field, '');
    } else {
      const parsed = Number(raw);
      if (Number.isFinite(parsed)) {
        const clamped = clampFieldPx(field, parsed * rootPx) / rootPx;
        const css = clamped <= 0.001 && clamped >= -0.001 ? '0' : `${clamped}rem`;
        commitValue(field, css);
      } else if (isValidSpacingCssValue(raw, field)) {
        commitValue(field, raw);
      } else {
        flashInvalidValue(field, input);
        return;
      }
    }
    refresh();
  }

  function cancelEdit() {
    activeEdit = null;
    refresh();
  }

  function onBandKeyDown(e, field) {
    if (activeEdit) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openPresetMenu(field);
      return;
    }
    if (e.key === 'F2') {
      e.preventDefault();
      beginEdit(field);
      return;
    }
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const direction = e.key === 'ArrowRight' ? 1 : -1;
      nudge(field, direction);
      return;
    }
    if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault();
      commitValue(field, '');
      return;
    }
  }

  function nudge(field, direction) {
    const fields = linked && LINK_PAIRS[field] ? [field, LINK_PAIRS[field]] : [field];
    const bases = captureBases(fields);
    if (!bases) {
      refuseUnmeasured();
      return;
    }
    if (!multi) store.beginTransaction('Adjust section spacing');
    applyRelative(fields, bases, direction * KEYBOARD_STEP_REM * rootPx, 'spacing-drawer-nudge');
    if (!multi) store.commitTransaction();
  }

  // ── Teardown ───────────────────────────────────────────────────────────
  return function teardown() {
    disposed = true;
    if (activeScrub?.handle) {
      try {
        activeScrub.handle.cancel();
      } catch {
        /* noop */
      }
    }
    if (activeEdit) {
      try {
        commitEdit();
      } catch {
        /* noop */
      }
    }
    eventBus.off('theme:override-changed', onOverrideChanged);
    eventBus.off('state-changed', onStateChanged);
    eventBus.off('viewport:changed', onResponsiveChanged);
    eventBus.off('responsive-edit-scope:changed', onResponsiveChanged);
    eventBus.off('iframe:bridge-ready', onBridgeReady);
    eventBus.off('iframe:section-spacing-response', onMeasurement);
  };
}

// ── DOM templates ───────────────────────────────────────────────────────

function renderBandHTML(field) {
  const kind = FIELD_KIND[field];
  const label = FIELD_LABELS[field];
  const sideLabel = field.endsWith('Top') ? 'Top' : 'Bottom';
  return `
    <div class="pb-spsd-band pb-spsd-band-${kind} pb-spsd-band-${field}"
         data-spsd-field="${field}"
         tabindex="0"
         role="spinbutton"
         aria-label="${escapeHtml(label)}, Measuring…">
      <span class="pb-spsd-band-side" aria-hidden="true">${sideLabel}</span>
      <span class="pb-spsd-band-label" aria-hidden="false">Measuring…</span>
      <span class="pb-spsd-band-unit" aria-hidden="true">rem</span>
    </div>
  `;
}

// Minimal inline SVG icons so the drawer is self-contained.
function chainOpenSvg() {
  return '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><path d="M6.5 9.5L9.5 6.5"/><path d="M5.5 4.5a2 2 0 0 0 0 4l1-1"/><path d="M10.5 11.5a2 2 0 0 0 0-4l-1 1"/></svg>';
}
function chainLockedSvg() {
  return '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 8h4"/><path d="M5 5.5a2.5 2.5 0 0 0 0 5h.5"/><path d="M11 5.5a2.5 2.5 0 0 1 0 5h-.5"/></svg>';
}
